import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './PromptGuide.css';

const RECIPES = [
  {
    id: 'deck',
    label: 'Build deck',
    subject: 'SKF',
    measures: 'spend, pricing, delivery, lead time, and problem parts',
    breakdown: 'issue and material',
    time: 'the trailing 12 months',
    delivery: 'Recommend decisions and corrective actions. Deliver a Wingman deck for the supplier review, including our commercial asks, why each is reasonable, and timing.',
  },
  {
    id: 'talking-points',
    label: 'Talking points',
    subject: 'SKF',
    measures: 'spend, pricing, delivery, lead time, problem parts, and open risks',
    breakdown: 'material and issue',
    time: 'the trailing 12 months',
    delivery: 'Prioritize what we should address on today\'s call. Deliver five concise talking points with the fact, why it matters, and the question or ask.',
  },
  {
    id: 'workbook',
    label: 'Excel workbook',
    subject: 'all suppliers',
    measures: 'original-promise-date coverage, late rate, late spend, average days late, and affected parts',
    breakdown: 'supplier and month',
    time: 'the trailing 12 months',
    delivery: 'Rank the highest-risk suppliers. Deliver an Excel workbook with summary, rankings, trends, detail, and data notes.',
  },
  {
    id: 'email',
    label: 'Email draft',
    subject: 'SKF',
    measures: 'late deliveries using original promise dates',
    breakdown: 'material and month',
    time: 'the trailing six months',
    delivery: 'Identify the main drivers and request a recovery plan. Deliver a collaborative but direct email with the evidence, requested action, owner, and response date.',
  },
  {
    id: 'brief',
    label: 'Executive brief',
    subject: 'the possible acquisition of a target company',
    measures: 'confirmed external information and our operational relationship',
    breakdown: 'confirmed facts, internal data, implications, and risks',
    time: 'the latest available information',
    delivery: 'Recommend continuity and negotiation actions. Deliver a one-page executive brief for leadership.',
  },
  {
    id: 'agenda',
    label: 'Meeting agenda',
    subject: 'SKF',
    measures: 'spend, pricing, delivery, lead time, major parts, and unresolved risks',
    breakdown: 'topic and priority',
    time: 'the trailing 12 months',
    delivery: 'Identify the decisions needed for next quarter. Deliver a 45-minute review agenda with objectives, topics, decisions, owners, and follow-ups.',
  },
  {
    id: 'action-plan',
    label: 'Action plan',
    subject: 'our single-sourced parts',
    measures: 'supplier, spend, and demand',
    breakdown: 'category and material',
    time: 'the trailing 12 months',
    delivery: 'Rank where we should qualify alternatives first. Deliver a 30-day action plan with priority, owner, next step, due date, evidence, and success measure.',
  },
  {
    id: 'opportunities',
    label: 'Opportunity list',
    subject: 'all suppliers',
    measures: 'spend, price changes, concentration, single-source exposure, delivery, lead time, and inventory risk',
    breakdown: 'supplier and material',
    time: 'the trailing 12 months',
    delivery: 'Rank the five priorities and explain the evidence, estimated impact, and confidence. Deliver an opportunity list for procurement leadership with an owner and next step for each item.',
  },
];

const FIELD_META = [
  {
    key: 'subject',
    number: '1',
    label: 'Subject',
    question: 'Who or what are you looking at?',
    hint: 'Supplier, material, category, site, or supplier group',
  },
  {
    key: 'measures',
    number: '2',
    label: 'Measures',
    question: 'What do you want to understand?',
    hint: 'Spend, price, delivery, lead time, inventory, demand, or risk',
  },
  {
    key: 'breakdown',
    number: '3',
    label: 'Breakdown',
    question: 'How should the result be organized?',
    hint: 'By supplier, material, month, category, site, or priority',
  },
  {
    key: 'time',
    number: '4',
    label: 'Time period',
    question: 'When should Wingman examine?',
    hint: 'Trailing 12 months, year to date, fiscal year, or exact dates',
  },
  {
    key: 'delivery',
    number: '5',
    label: 'Outcome & delivery',
    question: 'What should Wingman do and make?',
    hint: 'Rank, compare, explain, or recommend, then name the output and audience',
  },
];

function normalizeSentence(value) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

export function buildPrompt(fields) {
  const subject = fields.subject.trim() || 'this request';
  let analysis = `For ${subject}`;

  if (fields.measures.trim()) analysis += `, analyze ${fields.measures.trim()}`;
  if (fields.breakdown.trim()) analysis += `, broken down by ${fields.breakdown.trim()}`;
  if (fields.time.trim()) analysis += `, over ${fields.time.trim()}`;

  return [normalizeSentence(analysis), normalizeSentence(fields.delivery)]
    .filter(Boolean)
    .join(' ');
}

function PromptGuide({ writeToClipboard = (value) => navigator.clipboard.writeText(value) }) {
  const [activeRecipe, setActiveRecipe] = useState(RECIPES[0].id);
  const [fields, setFields] = useState({ ...RECIPES[0] });
  const [copyState, setCopyState] = useState('idle');
  const prompt = useMemo(() => buildPrompt(fields), [fields]);

  const chooseRecipe = (recipe) => {
    setActiveRecipe(recipe.id);
    setFields({ ...recipe });
    setCopyState('idle');
  };

  const updateField = (key, value) => {
    setActiveRecipe(null);
    setFields((current) => ({ ...current, [key]: value }));
    setCopyState('idle');
  };

  const copyPrompt = async () => {
    try {
      await writeToClipboard(prompt);
      setCopyState('copied');
    } catch (error) {
      setCopyState('error');
    }
  };

  return (
    <div className="pg">
      <div className="pg-shell">
        <header className="pg-hero">
          <Link className="pg-back" to="/">
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M12.5 4.5 7 10l5.5 5.5" />
            </svg>
            Back to chat
          </Link>
          <div className="pg-eyebrow">Wingman prompt guide</div>
          <h1>One structure. Any procurement request.</h1>
          <p>
            Give Wingman these five ingredients in order. Use the same path to explore an
            opportunity, understand a supplier, prepare a meeting, or create a finished output.
          </p>
          <p className="pg-hero-note">
            If an ingredient does not matter, leave it out. If you are unsure which output fits,
            ask Wingman to recommend one.
          </p>
          <div className="pg-formula" aria-label="The five prompt ingredients">
            {FIELD_META.map((field) => (
              <div className="pg-formula-item" key={field.key}>
                <span>{field.number}</span>
                {field.label}
              </div>
            ))}
          </div>
        </header>

        <main>
          <section className="pg-builder" aria-labelledby="pg-builder-heading">
            <div className="pg-section-head">
              <div>
                <div className="pg-kicker">Try the pattern</div>
                <h2 id="pg-builder-heading">Build a complete prompt</h2>
                <p>Choose a common output to see the pattern, then edit any field for your request.</p>
              </div>
            </div>

            <div className="pg-recipes" aria-label="Common output examples">
              {RECIPES.map((recipe) => (
                <button
                  type="button"
                  key={recipe.id}
                  className={activeRecipe === recipe.id ? 'is-active' : ''}
                  aria-pressed={activeRecipe === recipe.id}
                  onClick={() => chooseRecipe(recipe)}
                >
                  {recipe.label}
                </button>
              ))}
            </div>

            <div className="pg-builder-grid">
              <div className="pg-fields">
                {FIELD_META.map((field) => (
                  <label className="pg-field" key={field.key}>
                    <span className="pg-step-number">{field.number}</span>
                    <span className="pg-field-copy">
                      <span className="pg-field-title">
                        <strong>{field.label}</strong>
                        <span>{field.question}</span>
                      </span>
                      {field.key === 'delivery' ? (
                        <textarea
                          aria-label={field.label}
                          value={fields[field.key]}
                          onChange={(event) => updateField(field.key, event.target.value)}
                          rows="4"
                        />
                      ) : (
                        <input
                          aria-label={field.label}
                          type="text"
                          value={fields[field.key]}
                          onChange={(event) => updateField(field.key, event.target.value)}
                        />
                      )}
                      <small>{field.hint}</small>
                    </span>
                  </label>
                ))}
              </div>

              <aside className="pg-preview" aria-label="Prompt preview">
                <div className="pg-preview-label">Your prompt</div>
                <blockquote data-testid="prompt-preview">{prompt}</blockquote>
                <button type="button" className="pg-copy" onClick={copyPrompt}>
                  {copyState === 'copied' ? (
                    <>
                      <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4.5 10.5 3.2 3.2 7.8-8" /></svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 20 20" aria-hidden="true"><rect x="7" y="7" width="9" height="9" rx="2" /><path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /></svg>
                      Copy prompt
                    </>
                  )}
                </button>
                <div className="pg-copy-status" role="status" aria-live="polite">
                  {copyState === 'error' ? 'Copy failed. Select the prompt text and copy it manually.' : ''}
                </div>
                <div className="pg-preview-tip">
                  <strong>Quick check</strong>
                  <span>Subject · Measures · Breakdown · Time period · Outcome & delivery</span>
                </div>
              </aside>
            </div>
          </section>

          <section className="pg-reuse" aria-labelledby="pg-reuse-heading">
            <div>
              <div className="pg-kicker">Keep the analysis</div>
              <h2 id="pg-reuse-heading">Change the output without starting over</h2>
              <p>
                Once Wingman has completed the analysis, keep the first four ingredients and
                change only the fifth.
              </p>
            </div>
            <div className="pg-followups">
              <span>Using the same analysis, deliver it as five supplier talking points.</span>
              <span>Using the same analysis, draft a direct but collaborative supplier email.</span>
              <span>Using the same analysis, deliver an Excel workbook.</span>
              <span>Using the same analysis, prepare a Wingman deck for leadership.</span>
            </div>
          </section>

          <section className="pg-artifacts" aria-labelledby="pg-artifacts-heading">
            <div className="pg-section-head">
              <div>
                <div className="pg-kicker">For generated files</div>
                <h2 id="pg-artifacts-heading">Workbooks and decks add one review step</h2>
                <p>Chat outputs appear immediately. Generated files let you confirm the plan before Wingman builds.</p>
              </div>
            </div>
            <div className="pg-artifact-grid">
              <article>
                <div className="pg-artifact-title">
                  <span className="pg-file-icon" aria-hidden="true">XLSX</span>
                  <div><h3>Excel workbook</h3><p>Structured analysis you can download and reuse.</p></div>
                </div>
                <ol>
                  <li><span>1</span><div><strong>Ask for an Excel workbook</strong><p>Wingman answers the analysis first.</p></div></li>
                  <li><span>2</span><div><strong>Choose “Build your workbook”</strong><p>One click plans and builds it; a casual yes in chat does not.</p></div></li>
                  <li><span>3</span><div><strong>Download it or open Reports</strong><p>Completed workbooks and prior versions remain available.</p></div></li>
                </ol>
              </article>
              <article>
                <div className="pg-artifact-title">
                  <span className="pg-file-icon is-deck" aria-hidden="true">DECK</span>
                  <div><h3>Wingman deck</h3><p>A meeting-ready story for one supplier.</p></div>
                </div>
                <ol>
                  <li><span>1</span><div><strong>Ask for a Wingman deck</strong><p>Name one supplier, the audience, and the meeting purpose.</p></div></li>
                  <li><span>2</span><div><strong>Review the authored deck plan</strong><p>Confirm the supplier, audience, pages, focus, and time period.</p></div></li>
                  <li><span>3</span><div><strong>Add commercial asks</strong><p>State what you want, why it is reasonable, and the timing.</p></div></li>
                  <li><span>4</span><div><strong>Choose “Build deck”</strong><p>When the build finishes, choose “Open deck.”</p></div></li>
                </ol>
              </article>
            </div>
          </section>

          <section className="pg-check" aria-labelledby="pg-check-heading">
            <div>
              <div className="pg-kicker">Before you use the result</div>
              <h2 id="pg-check-heading">Check the evidence</h2>
            </div>
            <ul>
              <li>Confirm the supplier or subject and the time period are correct.</li>
              <li>Confirm definitions such as original versus current promise date.</li>
              <li>For a surprising figure, ask for the date range, population, denominator, and top rows behind it.</li>
              <li>Keep stated limitations, exclusions, and confidence with the recommendation.</li>
            </ul>
          </section>
        </main>
      </div>
    </div>
  );
}

export default PromptGuide;
